import React, { useContext, useMemo } from "react";
import { range } from "lodash";
import { unstable_batchedUpdates } from "react-dom";
import {
  type Attachment,
  attachmentsAreCompatible,
} from "common/utils/attachments";
import {
  computeDocumentUploaderDefaults,
  findDocumentTypeById,
} from "common/utils/formStructure";
import { spacing } from "../../../../../../../../stitches.config";
import { AuthContext } from "../../../../../../../Authorization/AuthContext";
import { BANNER_TYPES } from "../../../../../../../Common/__styles__/Banner";
import { FlexRow } from "../../../../../../../Common/__styles__/Layout";
import Banner from "../../../../../../../Common/Banner";
import { Select } from "../../../../../../../Inputs/react-hook-form";
import {
  getSchemaPropertyFormPath,
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
  useBuilderWatch,
} from "../../../form/form";
import { WidgetSettingsContainer } from "../__styles__/WidgetSettings";
import type { InputSettingsFormProps } from "../FieldSettingsView";
import { useIsFieldRequired } from "../required";
import { DisplaySettings } from "../shared/DisplaySettings";
import { FieldPermissionsConfig } from "../shared/FieldPermissionsConfig";
import { FieldSettingsConfig } from "../shared/FieldSettingsConfig";
import { HelperTextInput } from "../shared/HelperTextInput";
import { AdvancedSettings, CollapsibleSection } from "./AdvancedSettings";

export function DocumentUploadFieldSettings({
  fieldPath,
  isModuleInput,
  isReadOnly,
}: InputSettingsFormProps) {
  const { setValue } = useBuilderFormContext();

  const accountDocumentTypeOptions = useAccountDocumentTypeOptions();
  const accountDocumentTypeIdPath = getSchemaPropertyFormPath(
    fieldPath,
    "items.properties.accountDocumentTypeId.enum.0",
  );
  const accountDocumentTypeId = useBuilderWatch(accountDocumentTypeIdPath);

  const minItemsPath = getSchemaPropertyFormPath(fieldPath, "minItems");
  const maxItemsPath = getSchemaPropertyFormPath(fieldPath, "maxItems");

  const minItems = (useBuilderWatch(minItemsPath) ?? 0) as number;
  const maxItems = (useBuilderWatch(maxItemsPath) ?? 15) as number;

  const accountDocumentTypes = useAccountDocumentTypes();

  function onChangeDocumentType(value: Maybe<string>) {
    if (!value) {
      return;
    }

    const accountDocumentType = findDocumentTypeById(
      accountDocumentTypes,
      value,
    );
    const defaults = computeDocumentUploaderDefaults(
      accountDocumentType,
      minItems,
      maxItems,
    );

    unstable_batchedUpdates(() => {
      Object.entries(defaults).forEach(([key, value]) => {
        setValue(getUiSchemaPropertyFormPath(fieldPath, key), value, {
          shouldValidate: true,
        });
      });
    });
  }

  const isRequired = useIsFieldRequired(fieldPath);

  if (!accountDocumentTypeOptions.length) {
    return (
      <Banner type={BANNER_TYPES.INFO}>
        No compatible document types found. Please edit object type in the
        settings tab or update attachments for document types in Forest Admin.
      </Banner>
    );
  }

  return (
    <WidgetSettingsContainer>
      <FieldSettingsConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isReadOnly={isReadOnly}
        isFormulaField={false}
        onRequiredChange={e => {
          if (e.target.checked) {
            setValue(minItemsPath, 1, { shouldValidate: true });
          } else {
            setValue(minItemsPath, 0, { shouldValidate: true });
          }
        }}
      />
      <HelperTextInput fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <Select
        label="Document type"
        name={accountDocumentTypeIdPath}
        options={accountDocumentTypeOptions}
        disabled={isReadOnly}
        onChange={onChangeDocumentType}
        required
      />
      <FlexRow gap="m">
        <Select
          label="File upload minimum"
          name={minItemsPath}
          options={getMinMaxOptions({
            isMin: true,
            limit: maxItems,
          })}
          required={isRequired}
          disabled={isReadOnly || !isRequired}
        />
        <Select
          label="File upload maximum"
          name={maxItemsPath}
          options={getMinMaxOptions({
            isMin: false,
            // max of 0 is never valid
            limit: Math.max(minItems, 1),
          })}
          disabled={isReadOnly}
          required
        />
      </FlexRow>
      {!!accountDocumentTypeId && (
        <CollapsibleSection>
          <div style={{ marginTop: spacing.m.value }}>
            <AdvancedSettings fieldPath={fieldPath} isReadOnly={isReadOnly} />
          </div>
        </CollapsibleSection>
      )}
      <FieldPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <DisplaySettings fieldPath={fieldPath} isReadOnly={isReadOnly} />
    </WidgetSettingsContainer>
  );
}

type MinMaxOptions = {
  limit: number;
  isMin: boolean;
};

function getMinMaxOptions({ limit, isMin }: MinMaxOptions) {
  const min = isMin ? 1 : limit;
  const max = isMin ? limit : 25;

  // range is non-inclusive so we need to add 1 to the max
  return range(min, max + 1).map(value => ({
    label: value.toString(),
    value: value,
  }));
}

function useAccountDocumentTypes() {
  const { account } = useContext(AuthContext);
  return account?.accountDocumentTypes ?? [];
}

function useAccountDocumentTypeOptions() {
  const accountDocumentTypes = useAccountDocumentTypes();
  const submissionAttachments = useBuilderWatch("attachments");

  return useMemo(() => {
    return (
      accountDocumentTypes
        .map(accountDocumentType => ({
          label: accountDocumentType.name,
          value: accountDocumentType.id,
          attachments: accountDocumentType.attachments,
        }))
        .filter(accountDocumentType =>
          attachmentsAreCompatible({
            documentTypeAttachments:
              accountDocumentType.attachments as Attachment[],
            submissionAttachments,
          }),
        ) ?? []
    );
  }, [accountDocumentTypes, submissionAttachments]);
}
